import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  BatchPostingService,
  DateFormatterService,
  BatchPostingFileMetaDataRequestDto,
  BatchPostingFileStatusUpdate
} from 'remarketing-angular-library';
import { ModalComponent, SpinnerComponent, TrackDirective } from 'remarketing-ui-components-lib';
import { ErrorModalComponent } from '@ally/remarketing/shared/ui';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { take, takeUntil } from 'rxjs/operators';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import {
  NgbCalendar,
  NgbDate,
  NgbDateStruct,
  NgbInputDatepicker, NgbTimepicker
} from '@ng-bootstrap/ng-bootstrap';
import { BatchPostingFileMetaDataDto, BatchPostingFileStatus } from 'remarketing-angular-library/interfaces/dtos/batch-posting.dto';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-csv-update-widget',
    templateUrl: './csv-file-update-widget.component.html',
    styleUrls: ['./csv-file-update-widget.component.scss'],
    imports: [
        ReactiveFormsModule,
        NgbInputDatepicker,
        TrackDirective,
        NgbTimepicker,
        TranslocoDirective,
        SpinnerComponent,
        ErrorModalComponent
    ]
})
export class CsvFileUpdateWidgetComponent extends SaComponent implements OnInit {
  constructor(
    private readonly translocoService: TranslocoService,
    private readonly datePipe: DatePipe,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly batchPostingService: BatchPostingService,
    private ngbCalendar: NgbCalendar,
    public dateFormatter: DateFormatterService
  ) {
    super();
  }

  get fromDateTrack() {
    return this.trackCalendars
      ? CsvFileUpdateWidgetComponent.calendarTrackData('File Start Date Time')
      : { input: {}, button: {} };
  }

  userName: string;
  currentDate = new Date();
  @ViewChild('fileUpload') fileupload: ElementRef;
  @ViewChild('statusDateCalendar') statusDateCalendarButton: ElementRef;
  @ViewChild(ModalComponent) modal: ModalComponent;
  @ViewChild(ErrorModalComponent) errorModal: ErrorModalComponent;
  @Input() trackCalendars = false;
  @Output() hasDateRangeError = new EventEmitter<boolean>(false);
  @Input() selectedFile: BatchPostingFileMetaDataDto;

  file: File;
  fileName: string;
  fileType: string;
  statusDateValid = true;
  statusDateFormControl = new UntypedFormControl();
  statusMinDate: NgbDateStruct;
  statusStartDate: NgbDateStruct;
  today: NgbDateStruct = {
    year: this.ngbCalendar.getToday().year,
    month: this.ngbCalendar.getToday().month,
    day: this.ngbCalendar.getToday().day
  };
  displayStatusDate: string;
  date: { year: number; month: number; day: number };
  time: { hour: number; minute: number };
  selectedDateTime = '';
  isDisabled = false;
  selectedDate = '';
  appLoading = false;

  fileStatuses: BatchPostingFileStatus[];
  fileMetaDataRequest: BatchPostingFileMetaDataRequestDto;
  fileStatusUpdate: BatchPostingFileStatusUpdate;
  updateWidgetForm: UntypedFormGroup;
  selectedFileStatus: BatchPostingFileStatus;
  updateFileStatus = false;
  uploadInServiceVehicleShellTranslations = {
    cantCompleteRequestError: ''
  };
  errorMessage: string;

  private static calendarTrackData(name: string) {
    return {
      input: { name: `${name}`, elem: 'calendar' },
      button: { name: `${name}`, elem: 'calendar' }
    };
  }
  ngOnInit() {
    this.getTranslations();
    this.setInitialValues();
    this.updateWidgetForm.get('date')?.valueChanges.subscribe(() => {
      this.updateDateTime();
    });
    this.updateWidgetForm.get('time')?.valueChanges.subscribe(() => {
      this.updateDateTime();
    });
    this.updateDateTime();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.keep-span-visible')) {
      this.updateFileStatus = false;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab' || event.key === 'Enter') {
      this.updateFileStatus = false;
    }
  }

  getTranslations(): void {
    const keyParams = {
      error: {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.uploadInServiceVehicleShellTranslations.cantCompleteRequestError = translate[0].cantCompleteRequestError;
      });
  }

  setErrorMessage(error) {
    let isFirstError = true;
    let errorMessage = '';
    const errors = error.error.errors;
    this.errorMessage = '';
    if (errors) {
      errors.forEach(error => {
        errorMessage = error.detail;
        if (errorMessage.trim()) {
          if (!isFirstError) {
            // Delimit by new line if more than one error is thrown
            this.errorMessage += '\n';
          }
          this.errorMessage += errorMessage;
          isFirstError = false;
        }
      });
      // since innerHtml is used in error modal to render, need to replace new line with br to render accordingly
      this.errorMessage = this.errorMessage.replace(/\n/g, '<br>');
    } else {
      this.errorMessage = this.uploadInServiceVehicleShellTranslations.cantCompleteRequestError;
    }
  }

  updateDateTime() {
    const dateValue = this.selectedDate;
    const timeValue = this.updateWidgetForm.get('time')?.value;
    if (!dateValue) {
      const dateValueObj = this.updateWidgetForm.get('date')?.value;
      const { year, month, day } = dateValueObj;
      if (timeValue) {
        let { hour, minute } = timeValue;
        if (!hour) {
          hour = new Date().getHours() + 2;
        }
        if (!minute) {
          minute = new Date().getMinutes();
        }
        this.selectedDateTime = `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day} ${hour}:${
          minute < 10 ? '0' + minute : minute
        }`;
      } else {
        this.selectedDateTime = `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day} 10:0}`;
      }
    } else {
      if (timeValue) {
        let { hour, minute } = timeValue;
        if (!hour) {
          hour = new Date().getHours() + 2;
        }
        if (!minute) {
          minute = new Date().getMinutes();
        }
        this.selectedDateTime = `${dateValue} ${hour}:${minute < 10 ? '0' + minute : minute}`;
      } else {
        this.selectedDateTime = `${dateValue} 10:0`;
      }
    }
  }

  focusStatusDateCalendar() {
    this.statusDateCalendarButton.nativeElement.focus();
  }

  setInitialValues() {
    this.statusMinDate = { year: this.today.year, month: this.today.month, day: this.today.day };
    this.updateWidgetForm = this.formBuilder.group({
      date: this.datePipe.transform(new Date(), 'MM/dd/yyyy'),
      time: { hour: 10, minutes: 0 },
      fileStatus: 'default'
    });
    const fromValue = NgbDate.from(this.dateFormatter.sanitizeDateInput(this.today));
    const fromDateString = this.dateFormatter.localizeDateToString(fromValue);
    const transformedDate = this.datePipe.transform(new Date(fromDateString), 'yyyy-MM-dd');
    this.selectedDate = transformedDate;
    if (!this.fileMetaDataRequest) {
      const currentDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
      this.fileMetaDataRequest = { date: currentDate };
    }
    this.batchPostingService
      .getBatchPostingFileStatus()
      .pipe(take(1))
      .subscribe(statuses => {
        this.fileStatuses = statuses;
      });
    this.displayStatusDate = this.datePipe.transform(new Date(), 'MM-dd-yyyy');
  }

  setSelectedFileStatus() {
    this.selectedFileStatus = this.fileStatuses.filter(
      fileStatus => fileStatus === this.updateWidgetForm.get('fileStatus').value
    )[0];
  }

  processFileUpdate() {
    const [datePart, timePart] = this.selectedDateTime.split(' ');
    const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
    const [hour, minute] = timePart.split(':').map(num => parseInt(num, 10));
    const date = new Date(year, month - 1, day, hour, minute);
    const isoDateString = date.toISOString();
    if (this.selectedFileStatus && isoDateString) {
      this.fileStatusUpdate = {
        status: this.selectedFileStatus,
        startTime: isoDateString
      };
      this.appLoading = true;
      this.batchPostingService.updateBatchFileStatus(this.selectedFile.fileUUid, this.fileStatusUpdate).subscribe({
        next: () => {
          this.updateFileStatus = true;
          this.appLoading = false;
        },
        error: error => {
          console.error(error);
          if (error.error.errors) {
            this.setErrorMessage(error);
            this.errorModal.message = this.errorMessage;
            this.errorModal.show();
          } else {
            // when service is down or timing out waiting for response, to show generic error.
            this.errorMessage = this.uploadInServiceVehicleShellTranslations.cantCompleteRequestError;
            this.errorModal.message = this.errorMessage;
            this.errorModal.show();
          }
          this.updateFileStatus = false;
        }
      });
    } else {
      this.errorModal.message = 'Please select file status and start date/time';
      this.errorModal.show();
    }
  }

  updateStatusDate(statusDate: NgbDate | NgbDateStruct) {
    const fromValue = NgbDate.from(this.dateFormatter.sanitizeDateInput(statusDate));
    if (statusDate && this.validateStatusDateValue(statusDate)) {
      this.statusStartDate = fromValue ? fromValue : this.statusStartDate;
      this.displayStatusDate = this.dateFormatter.localizeDateToString(this.statusStartDate);
      this.statusDateValid = true;
      const transformedDate = this.datePipe.transform(new Date(this.displayStatusDate), 'yyyy-MM-dd');
      this.selectedDate = transformedDate;
      this.updateDateTime();
    } else {
      this.statusDateValid = false;
    }
    this.hasDateRangeError.emit(!this.statusDateValid);
  }

  validateStatusDateValue(statusDate): boolean {
    const statusDateValue = this.dateFormatter.sanitizeDateInput(statusDate);

    // if no date provided then consider current date and hence no error to be returned.
    if (statusDate === undefined) return true;

    if (
      statusDateValue &&
      statusDateValue.month !== null &&
      statusDateValue.day !== null &&
      statusDateValue.year !== null
    ) {
      const fromDate = new NgbDate(statusDateValue.year, statusDateValue.month, statusDateValue.day);
      const isValid = this.ngbCalendar.isValid(fromDate);
      return isValid;
    } else {
      return false;
    }
  }
}
